import { money } from "@yn/money";
import { MoneyOptions } from "@yn/money";
import BigMath from "@yn/money/math";
import { CurrencyCode } from "@yn/money/types";
import { calc_wrap } from "a-calc";
import dayjs from "dayjs";
import lodashGet from "lodash/get";

export const USD_SYMBOL = "$";
export const CENT_SYMBOL = "¢";

/**
 * 增强版 get 函数
 * 1. 支持多路径取值：get(obj, "path1 | path2 | path3", default) - 按顺序尝试取值
 * 2. 支持多数据源：get([obj1, obj2, obj3], "path", default) - 按顺序从多个对象取值
 * 3. 两者可组合：get([obj1, obj2], "path1 | path2", default)
 */
function get_wrap( ...args: any[] ): any {
    const source = args[0];
    const pathStr = args[1];
    const defaultValue = args.length === 3 ? args[2] : null;

    // 解析多路径
    const paths = pathStr.split( "|" ).map( ( p: string ) => p.trim() );

    // 获取数据源数组
    const sources = Array.isArray( source ) ? source : [ source ];

    // 遍历数据源和路径，返回第一个有效值
    for ( const src of sources ) {
        for ( const path of paths ) {
            const ret = lodashGet( src, path, null );
            if ( ret !== null && ret !== undefined ) {
                return ret;
            }
        }
    }

    return defaultValue;
}

interface FormatCurrencyOptions {
    showSymbol?: boolean;
    symbol?: string;
    withSpace?: boolean;
    minDecimals?: number;
    maxDecimals?: number;
}

/**
 * Format currency with thousands separator and flexible decimal places
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(1234.5) // "$1,234.5"
 * formatCurrency(1234.567, { minDecimals: 2, maxDecimals: 2 }) // "$1,234.57"
 * formatCurrency(1234, { showSymbol: false }) // "1,234"
 * formatCurrency(1234.5, { withSpace: true }) // "$ 1,234.5"
 */
export function formatCurrency( amount: number | string, options: FormatCurrencyOptions = {} ): string {
    const { showSymbol = true, symbol = USD_SYMBOL, withSpace = false, minDecimals = 0, maxDecimals = 2 } = options;

    // Convert to number
    const numAmount = typeof amount === "string" ? parseFloat( amount ) : amount;

    // Handle invalid numbers
    if ( Number.isNaN( numAmount ) ) {
        return showSymbol ? `${symbol}${withSpace ? " " : ""}0` : "0";
    }

    // Format with specified decimal places
    const formatted = new Intl.NumberFormat( "en-US", {
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
    } ).format( numAmount );

    // Add currency symbol if needed
    if ( showSymbol ) {
        return `${symbol}${withSpace ? " " : ""}${formatted}`;
    }

    return formatted;
}

/**
 * Convert cents to dollars and format as currency
 * @param cents - The amount in cents (string or number)
 * @param options - Formatting options (same as formatCurrency)
 * @returns Formatted currency string in dollars
 *
 * @example
 * formatCentsToDollars(1234) // "$12.34"
 * formatCentsToDollars("5000", { minDecimals: 2, maxDecimals: 2 }) // "$50.00"
 */
export function formatCentsToDollars( cents: number | string ): string {
    return calc( "balance | =2!ub:$", { balance: cents } );
}

function calc( expr: string, data: any = {} ): any {
    const oldExpr = expr;
    expr = expr.replace?.( /!ua:|!u:/, "!ub:" );
    expr = expr ?? oldExpr;
    const options = { _unit_out: { $: { "¢": 0.01 } } };
    if ( Array.isArray( data ) ) {
        return calc_wrap( expr, [ options, ...data ] );
    }
    return calc_wrap( expr, { ...options, ...data } );
}

/**
 * Handle redirect URL navigation
 * @param redirectUrl - The redirect URL to navigate to
 * @param router - Next.js router instance (from useRouter hook)
 * @returns true if navigation was attempted, false otherwise
 */
export function handleRedirectUrl(
    redirectUrl: string,
    router: { push: ( url: string ) => void | Promise<void> },
): boolean {
    const trimmedUrl = redirectUrl.trim();

    // Return false if URL is empty
    if ( !trimmedUrl ) {
        return false;
    }

    // Internal route (starts with /)
    if ( trimmedUrl.startsWith( "/" ) ) {
        try {
            void router.push( trimmedUrl );
            return true;
        } catch {
            // Silently fail if navigation fails
            return false;
        }
    }

    // External link (http/https)
    if ( trimmedUrl.startsWith( "http://" ) || trimmedUrl.startsWith( "https://" ) ) {
        try {
            // Validate URL format
            new URL( trimmedUrl );
            // Navigate to external link
            window.open( trimmedUrl, "_blank" );
            return true;
        } catch {
            // Invalid URL, don't navigate
            return false;
        }
    }

    // Not a valid redirect URL format
    return false;
}

/**
 * Check if redirect URL is supported (without performing navigation)
 * @param redirectUrl - The redirect URL to validate
 * @returns true if redirectUrl is supported, false otherwise
 */
export function isRedirectUrlSupported( redirectUrl: string ): boolean {
    const trimmedUrl = redirectUrl.trim();
    if ( !trimmedUrl ) {
        return false;
    }

    if ( trimmedUrl.startsWith( "/" ) ) {
        return true;
    }

    if ( trimmedUrl.startsWith( "http://" ) || trimmedUrl.startsWith( "https://" ) ) {
        try {
            new URL( trimmedUrl );
            return true;
        } catch {
            return false;
        }
    }

    return false;
}

export { calc, get_wrap as get };

/**
 * Guess 选项标签输入接口（最小字段）
 */
export interface GuessOptionLabelInput {
    guessNo: string; // 必需：guess 编号
    mainFlag?: number | boolean; // 可选：是否主竞猜
    yesDesc?: string; // 可选：Yes 选项描述
    noDesc?: string; // 可选：No 选项描述
}

/**
 * 对阵选项标签输入接口（最小字段）
 */
export interface MatchupOptionLabelInput {
    abbr?: string; // 可选：对阵缩写（用于显示标签）
}

/**
 * 获取竞猜选项的显示文本（Yes/No 或自定义描述）
 * @param option - 选项类型 "yes" 或 "no"
 * @param guess - 竞猜信息对象
 * @param detailInfo - 体育赛事详情（可选，支持单个对象或多数据源数组）
 * @returns 选项显示文本
 *
 * 取值逻辑：
 * - mainFlag 为 true 且有 detailInfo：优先从 matchups[index].abbr 取，否则从 guesses[0] 或 guessList[0] 取
 * - mainFlag 为 false 且 useDescFallback 为 true：从 guess 的 yesDesc/noDesc 取
 * - 其他情况：返回默认值 "Yes"/"No"
 *
 * @example
 * // 体育赛事主卡片（mainFlag=true，单数据源）
 * getOptionLabel("yes", guess, sportsDetail)
 *
 * // 多数据源（按顺序尝试）
 * getOptionLabel("yes", guess, [detailInfo, eventInfo])
 *
 * // 普通卡片
 * getOptionLabel("yes", guess)
 *
 * // 使用 yesDesc/noDesc 回退
 * getOptionLabel("yes", guess, sportsDetail)
 */
export function getOptionLabel( option: "yes" | "no", guess: any, _detailInfo?: any | any[] ): string {
    const defaultLabel = option === "yes" ? "Yes" : "No";
    const descKey = option === "yes" ? "yesDesc" : "noDesc";
    return guess?.[descKey] || defaultLabel;
}

// 老方法 getOptionLabel，保留
export function getOptionLabelOld( option: "yes" | "no", guess: any, detailInfo?: any | any[] ): string {
    const isMainFlag = get_wrap( guess, "mainFlag", false );
    const defaultLabel = option === "yes" ? "Yes" : "No";
    const descKey = option === "yes" ? "yesDesc" : "noDesc";
    const matchupIndex = option === "yes" ? 0 : 1;
    // 如果是聚合竞猜，自动返回yes和no
    if ( guess.guessGroupNo ) {
        return defaultLabel;
    }

    if ( isMainFlag && detailInfo ) {
        // mainFlag 为 true，从 matchups 或 guesses/guessList 取值
        // 支持多数据源（数组或单个对象）
        const sources = Array.isArray( detailInfo ) && !( "matchups" in detailInfo )
            ? [ ...detailInfo, guess ]
            : [ detailInfo, guess ];
        return get_wrap(
            sources,
            `matchups.${matchupIndex}.abbr | guesses.0.${descKey} | guessList.0.${descKey}`,
            get_wrap( guess, descKey, defaultLabel ),
        );
    }

    return get_wrap( Array.isArray( detailInfo ) ? [ ...detailInfo, guess ] : guess, descKey, "" ) || defaultLabel;
}

/**
 * 根据 guessNo 获取竞猜选项的显示文本（简化封装方法）
 * @param guessList - guess 列表（只需包含最小字段）
 * @param guessNo - 要查找的 guess 编号
 * @param option - 选项类型 "yes" 或 "no"
 * @param matchups - 可选，对阵信息数组（最小字段：abbr）
 * @returns 选项显示文本
 *
 * 取值逻辑：
 * - mainFlag 为真时：优先使用 matchups[index].abbr，如果没有则回退到 yesDesc/noDesc，再没有则回退到 "Yes"/"No"
 * - mainFlag 为假时：只考虑 yesDesc/noDesc -> "Yes"/"No"，不考虑 matchups
 *
 * @example
 * // 使用最小字段
 * getOptionLabelByGuessNo(guessList, "GS123", "yes", matchups)
 */
export function getOptionLabelByGuessNo(
    guessList: GuessOptionLabelInput[],
    guessNo: string,
    option: "yes" | "no",
    matchups?: MatchupOptionLabelInput[],
): string {
    // 根据 guessNo 查找对应的 guess 对象
    const guess = guessList.find( ( g ) => g.guessNo === guessNo );

    // 如果找不到，返回默认值
    if ( !guess ) {
        return option === "yes" ? "Yes" : "No";
    }

    // 定义默认标签
    const defaultLabel = option === "yes" ? "Yes" : "No";
    // 定义描述字段
    const descKey = option === "yes" ? "yesDesc" : "noDesc";
    // 定义 matchup 索引
    const matchupIndex = option === "yes" ? 0 : 1;

    // 判断 mainFlag 是否为真（true/1）
    const isMainFlag = guess.mainFlag === true || guess.mainFlag === 1;

    if ( isMainFlag ) {
        // mainFlag 为真：优先使用 matchups[index].abbr
        if ( matchups && matchups[matchupIndex]?.abbr ) {
            return matchups[matchupIndex].abbr;
        }
        // 否则，如果 guess[descKey] 存在，返回该值
        if ( guess[descKey] ) {
            return guess[descKey];
        }
        // 否则，返回默认标签
        return defaultLabel;
    }

    // mainFlag 为假：只考虑 yesDesc/noDesc
    if ( guess[descKey] ) {
        return guess[descKey];
    }
    // 否则，返回默认标签
    return defaultLabel;
}

type MoneyFormatCurrencyOptions = MoneyOptions & { currency?: CurrencyCode; targetCurrency?: CurrencyCode };

export function moneyFormatCurrencyWith( amount: number | string, moneyOptions: MoneyFormatCurrencyOptions = {} ) {
    const { currency = "INR", targetCurrency, useExchange, ...formatOptions } = moneyOptions;
    const resolvedUseExchange = useExchange === true && typeof targetCurrency === "string" && !!currency;
    const resolvedTargetCurrency = resolvedUseExchange ? ( targetCurrency as CurrencyCode ) : currency;
    if ( resolvedUseExchange && typeof window !== "undefined" ) {
        ( money as any )?.refreshExchangeRatesFor?.( { currency, targetCurrency: targetCurrency as CurrencyCode } );
    }
    return money.convertAndFormat( { amount: Number( amount ), currency }, resolvedTargetCurrency, {
        ...formatOptions,
        useExchange: resolvedUseExchange,
    } );
}

export function moneyFormatCurrency( amount: number | string, moneyOptions: MoneyFormatCurrencyOptions = {} ) {
    return moneyFormatCurrencyWith( amount, moneyOptions );
}

export function formatUnitPrice( amount: number | string, moneyOptions: MoneyFormatCurrencyOptions = {} ) {
    return moneyFormatCurrency( amount, { currency: "CENT", useExchange: false, ...moneyOptions, precision: 0 } );
}
// 美分转美元
export function formatMonetaryValue( amount: number | string, moneyOptions: MoneyFormatCurrencyOptions = {} ) {
    return moneyFormatCurrency( amount, {
        currency: "CENT",
        targetCurrency: "USD",
        useExchange: true,
        ...moneyOptions,
    } );
}

export {
    attachMoneyExchangeUpdater,
    refreshMoneyExchangeRates,
    refreshMoneyExchangeRatesFor,
    registerExchangeRateFetcher,
    updateMoneyExchangeRates,
} from "./money-exchange";

export function remToPx( rem: number ) {
    if ( typeof document === "undefined" ) return rem * 16;
    return rem * parseFloat( getComputedStyle( document.documentElement ).fontSize );
}

/**
 * 格式化时间戳为 "MMM D, YYYY, h:mma" 格式
 * @param timestamp - 时间戳 (毫秒或秒)
 * @returns 格式化后的时间字符串
 * 示例: Dec 4, 2025, 7:47pm
 */
export function formatDate( timestamp: number | string | Date ): string {
    if ( !timestamp ) return "";

    let date = dayjs( timestamp );

    // 如果是 10 位数字（秒级时间戳），通常需要处理
    if ( typeof timestamp === "number" && timestamp.toString().length === 10 ) {
        date = dayjs.unix( timestamp );
    }

    return date.format( "MMM D, YYYY, h:mma" );
}

/**
 * 通用文件下载方法
 * @param url 文件链接或 Blob 对象
 * @param fileName 下载后的文件名
 */
export async function downloadFile( url: string | Blob, fileName: string ) {
    let downloadUrl = "";
    let isBlob = false;

    if ( url instanceof Blob ) {
        downloadUrl = URL.createObjectURL( url );
        isBlob = true;
    } else {
        downloadUrl = url;
    }

    try {
        const link = document.createElement( "a" );
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild( link );
        link.click();
        document.body.removeChild( link );

        if ( isBlob ) {
            // 给一点点延迟再释放，确保浏览器已经开始下载
            setTimeout( () => URL.revokeObjectURL( downloadUrl ), 100 );
        }
    } catch ( error ) {
        console.error( "Download failed:", error );
        throw error;
    }
}

export const sleep = ( ms: number ) => new Promise( ( resolve ) => setTimeout( resolve, ms ) );

export function ConvertOdds( val: number | string | null | undefined ): string {
    return `${BigMath.toFixed( BigMath.div( 1, val ), 2 )}×`;
}
